import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { GetJson, PutJson } from '@awp/helpers/fetchReact';
import { AWPLocalDB } from '@components/Common/AWPLocalDB';
import { OfflineFormStatus, FORM_MODE, EventTypes, Permissions, REST_API, NULL_OPTION } from '@awp/constants';
import * as Yup from 'yup';
import {
  getAvalancheObservations,
  getAvalancheGeographicAreas,
  getAvalancheGeographicPaths,
  selectTargetPointsDd,
} from '@awp/stores/avalancheSlice';

import {
  getOptionalEventPropertiesByTypeName,
  getEventPropertiesByTypeName,
  selectEventTypes,
} from '@awp/stores/eventSlice';

import { selectCurrentUser } from '@awp/stores/usersSlice';
import { selectAvalancheTechnicians } from '@awp/stores/dropdownsSlice';
import { uuidv4 } from '@awp/helpers/Utils';
import { Card, Spinner, Button, Container, Row } from 'react-bootstrap';

import {
  FormTextArea,
  FormSelect,
  FormDatePicker,
  FormCheckbox,
  FormGpsPoint,
  FormTextReadOnly,
} from '@components/Common/AwpFormInputs';
import { hasAccess } from '@components/Authorize';

import { defaultControlValidations, generateOptionalValidation } from './OccurrenceValidation';
import { OccurrenceOptionalFields } from './OptionalFields';
import { mapFormToServerFormat, mapServerToFormFormat } from './OccurrenceHelper';

import { Formik, Form } from 'formik';
import { getInitialTouched, getUtcToLocal, isValueEmpty } from '@awp/helpers/utilsReact';
import { toast } from 'react-toastify';
import AwpYesNoModal from '@components/Common/AwpYesNoModal';

import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import { useAvalancheEventContext } from './AvalanceEventContext';
import ProcessedCriticalFieldsEdit from './ProcessedCriticalFieldsEdit';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import {
  checkEventType,
  findEventTypeNmById,
  isCriticalFieldsUpdated,
  locationCriticalFieldsToEntity,
} from './ProcessedCriticalFieldsHelper';
import { validateBcCoordinates } from '@awp/helpers/validation';

const AvalancheControlEdit = ({
  setMode,
  mode,
  setEventId,
  eventId,
  setLoading,
  loading,
  fullData,
  setEventTypeToActOn,
  isProcessed,
  isRoute,
  isDuplicated,
  createXnOccurrence,
  fromMenu,
  findEvent,
  breadcrumbName,
}) => {
  let fullContextData;
  try {
    fullContextData = useAvalancheEventContext()?.fullContextData;
  } catch (e) {
    fullContextData = null;
  }

  const loadedData = fullContextData ?? fullData;

  const currentUser = useSelector(selectCurrentUser);
  const location = useLocation();
  const navigate = useNavigate();
  const controlEventProperties = useSelector((state) =>
    getOptionalEventPropertiesByTypeName(state, EventTypes.AvalancheControl),
  );

  const allControlEventProperties = useSelector((state) =>
    getEventPropertiesByTypeName(state, EventTypes.AvalancheControl),
  );
  const observationFields = allControlEventProperties.map((x) => x.ObservationName);

  const controlTypes = useSelector((state) => getAvalancheObservations(state, 'aval_cntrl_type_cd'));
  const programAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));
  const avalanchePaths = useSelector((state) => getAvalancheGeographicPaths(state));
  const avalancheTechs = useSelector((state) => selectAvalancheTechnicians(state));
  const areaPoints = useSelector((state) => selectTargetPointsDd(state));
  const eventTypes = useSelector(selectEventTypes);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const defaultDto = {
    avalanche_program: null,
    avalanche_area: null,
    avalanche_path: null,
    avalanche_target: null,
    aval_cntrl_type_cd: null,
    aval_is_detonation_failure: false,
    aval_is_avalanche_success: false,
    aval_is_outside: false,
    blaster: avalancheTechs.options.find((t) => t.value === currentUser.SecurityIdentifierId) || null,
    assistantBlaster: null,
    timestamp: new Date(),
    comment: '',
  };
  const [dto, setDto] = useState(defaultDto);
  const [eventErrors, setEventErrors] = useState(null);
  const [formStatus, setFormStatus] = useState(null);

  const avalanchePrograms = mode === FORM_MODE.CREATE ? programAreas.filter((x) => !x.isDisabled) : programAreas;
  const [areaList, setAreaList] = useState(
    mode === FORM_MODE.CREATE ? avalancheAreas.filter((x) => !x.isDisabled) : avalancheAreas,
  );
  const [pathsList, setPathsList] = useState(
    mode === FORM_MODE.CREATE ? avalanchePaths.filter((x) => !x.isDisabled) : avalanchePaths,
  );
  const [pointList, setPointList] = useState([]);
  const [isAvalancheTech, setIsAvalancheTech] = useState(true);
  const [isDisableTargeDd, setIsDisableTargeDd] = useState(true);
  const [originalValues, setOriginalValues] = useState(null);
  const [showCriticalFieldsModal, setShowCriticalFieldsModal] = useState(false);
  const [showCriticalFields, setShowCriticalFields] = useState(false);
  const [criticalFields, setCriticalFields] = useState(null);
  const [loadingCriticalField, setLoadingCriticalField] = useState(false);
  const [eventPaths, setEventPaths] = useState(null); // e.g. RC or Phased events
  const [eventSegment, setEventSegment] = useState(null);
  const [eventFrom, setEventFrom] = useState(null);
  const [eventTo, setEventTo] = useState(null);
  const [eventLocations, setEventLocations] = useState(null); // == non-eventPaths e.g. AC or AO
  const nullOption = NULL_OPTION;

  const readOnly = mode !== FORM_MODE.EDIT && mode !== FORM_MODE.CREATE;
  const buttonLabel = 'Save';

  const title =
    mode === FORM_MODE.EDIT
      ? isProcessed
        ? 'Edit Processed Avalanche Control'
        : 'Edit Avalanche Control'
      : mode === FORM_MODE.CREATE
      ? isProcessed
        ? 'Create Processed Avalanche Control'
        : 'Create Avalanche Control'
      : isProcessed
      ? 'Processed Avalanche Control'
      : 'Avalanche Control';

  const gpsPointValidations = () => {
    let schema = Yup.object({
      longitude: Yup.number()
        .label('Longitude')
        .nullable()
        .test('is-bc-coordinates', 'Invalid British Columbia Coordinates', function (value) {
          return validateBcCoordinates(value, this.parent.latitude);
        }),
      latitude: Yup.number()
        .label('Latitude')
        .nullable()
        .test('is-bc-coordinates', 'Invalid British Columbia Coordinates', function (value) {
          return validateBcCoordinates(this.parent.longitude, value);
        }),
    });

    return schema;
  };
  const requiredFieldMessage = '${label} is a required field';
  const processedLocationValidations = () => {
    let schema = Yup.object({
      avalanche_path: Yup.object()
        .label('Path')
        .nullable()
        .test('Path', 'Avalanche Path is required', (value, context) => {
          if (context.parent.avalanche_path && context.parent.avalanche_path.value) return true;
          return false;
        }),
      avalanche_area: Yup.object()
        .label('Avalanche Area')
        .nullable()
        .test('AvalancheArea', 'Avalanche Area is required', (value, context) => {
          if (context.parent.avalanche_area && context.parent.avalanche_area.value) return true;
          return false;
        }),
    });

    return schema;
  };

  const optionalValidation = generateOptionalValidation(controlEventProperties);
  const defaultValidation = defaultControlValidations();
  const validationSchema =
    isProcessed && mode === FORM_MODE.EDIT
      ? defaultValidation
          .concat(optionalValidation)
          .concat(gpsPointValidations())
          .concat(processedLocationValidations())
      : defaultValidation.concat(optionalValidation).concat(gpsPointValidations());

  const applyUserPreferences = () => {
    const data = (JSON.parse(currentUser.Preferences) ?? {})?.control;

    if (!data) return;

    const dto = defaultDto;

    if (data.program) {
      dto.avalanche_program = programAreas.find((x) => x.value == data.program && !x.isDisabled);
      dto.avalanche_program && setAreaList(filterList(avalancheAreas, data.program));
    }

    if (dto.avalanche_program && data.area) {
      dto.avalanche_area = avalancheAreas.find((x) => x.value == data.area && !x.isDisabled);
      dto.avalanche_area && setPathsList(filterList(pathsList, data.area));
    }

    if (dto.avalanche_program && dto.avalanche_area && data.path) {
      dto.avalanche_path = pathsList.find((x) => x.value == data.path && !x.isDisabled);
      if (dto.avalanche_path) {
        let ap = dto.avalanche_path;

        if (ap) {
          filterTargetList(ap);
          setIsDisableTargeDd(false);
          filterTargetList(ap);
        }
      }
    }

    if (data.aval_cntrl_type_cd) {
      dto.aval_cntrl_type_cd = controlTypes.find((x) => x.value == data.aval_cntrl_type_cd);
    }

    dto.aval_cntrl_chrg = data.aval_cntrl_chrg;
    dto.aval_cntrl_cast_booster = data.aval_cntrl_cast_booster;
    dto.aval_cntrl_fuse_no = data.aval_cntrl_fuse_no;
    dto.aval_cntrl_fuse_length = data.aval_cntrl_fuse_length;

    data.blaster && (dto.blaster = avalancheTechs.options.find((x) => x.value == data.blaster));
    data.assistantBlaster &&
      (dto.assistantBlaster = avalancheTechs.options.find((x) => x.value == data.assistantBlaster));

    setDto(dto);
  };

  useEffect(() => {
    if (mode === FORM_MODE.CREATE || mode === FORM_MODE.EDIT) {
      setIsAvalancheTech(avalancheTechs.options.findIndex((t) => t.value === currentUser.SecurityIdentifierId) !== -1);
    }
    if (mode === FORM_MODE.CREATE) {
      applyUserPreferences();
      setFormStatus(OfflineFormStatus.Invalid);
      return;
    }
    if (mode === FORM_MODE.EDIT && !eventId) {
      return;
    }
    setLoading(true);
    if (isRoute) {
      fetchRouted();
    } else {
      if (isProcessed) {
        fetchProcessed();
      } else {
        fetchUnprocessed();
      }
    }
  }, []);
  const setUnprocessedEvent = (entity) => {
    const dtoTemp = mapServerToFormFormat(entity);
    setDto(dtoTemp);
    if (isRoute) {
      setFormStatus(dtoTemp.status);
    } else {
      setFormStatus(entity.status);
    }

    //if the prog/area dropdowns have selected values then we need to
    // ensure the filtering is initialized
    if (dtoTemp['avalanche_program']) {
      setAreaList(filterList(avalancheAreas, dtoTemp['avalanche_program'].value));
    }
    if (dtoTemp['avalanche_area']) {
      setPathsList(filterList(avalanchePaths, dtoTemp['avalanche_area'].value));
    }
    let ap = dtoTemp['avalanche_path'];
    if (ap) {
      filterTargetList(ap);
      setIsDisableTargeDd(false);
      //setFieldValue('avalanche_target', '', false);
      filterTargetList(ap);
    }
  };
  const fetchUnprocessed = () => {
    AWPLocalDB.avalancheData
      .get(eventId)
      .then((entity) => {
        if (!entity) {
          entity = findEvent(loadedData, eventId);
        } else {
          entity.internal.timestamp = new Date(entity.internal.timestamp);
        }
        setUnprocessedEvent(entity);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const fetchRouted = () => {
    const url = REST_API.INGESTION_CANDIDATE_BY_TRACEID + eventId;
    GetJson(url)
      .then((response) => response.json())
      .then((occurrence) => {
        if (occurrence && !isValueEmpty(occurrence.JsonObject)) setUnprocessedEvent(JSON.parse(occurrence.JsonObject));
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const fetchProcessed = () => {
    const url = REST_API.PROCESSED_CONTROL + eventId;

    GetJson(url)
      .then((response) => response.json())
      .then((data) => {
        afterFetchProcessed(data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const afterFetchProcessed = (data) => {
    data.timestamp = new Date(data.timestamp);
    data.lastUpdatetimestamp = data.lastUpdatetimestamp ? getUtcToLocal(data.lastUpdatetimestamp + '') : '';
    setDto(data);
    setOriginalValues(data);
    setFormStatus(data.status);

    //if the prog/area dropdowns have selected values then we need to
    // ensure the filtering is initialized
    if (data['avalanche_program']) {
      setAreaList(filterList(avalancheAreas, data['avalanche_program'].value));
    }
    if (data['avalanche_area']) {
      setPathsList(filterList(avalanchePaths, data['avalanche_area'].value));
    }
    let ap = data['avalanche_path'];
    if (ap) {
      filterTargetList(ap);
      setIsDisableTargeDd(false);
      filterTargetList(ap);
    }
  };

  const programOnChange = (opt, setFieldValue) => {
    if (isProcessed && mode === FORM_MODE.EDIT) return programOnProcessedChange(opt, setFieldValue);
    setFieldValue('avalanche_area', nullOption, false);
    setFieldValue('avalanche_path', nullOption, false);
    setAreaList(avalancheAreas.filter((x) => x.parentId === opt.value));

    //path list is unfiltered
    setPathsList(avalanchePaths);
    setFieldValue('avalanche_target', nullOption, false);
    setPointList([]);
    setIsDisableTargeDd(true);
  };
  const programOnProcessedChange = (opt, setFieldValue) => {
    setFieldValue('avalanche_area', nullOption, false);
    setFieldValue('avalanche_path', nullOption, false);
    setAreaList(avalancheAreas.filter((x) => x.parentId === opt.value));

    //path list is unfiltered
    setPathsList(avalanchePaths);
    setFieldValue('avalanche_target', nullOption, false);
    setPointList([]);
    setIsDisableTargeDd(true);
  };

  const areaOnChange = (opt, setFieldValue) => {
    if (isProcessed && mode === FORM_MODE.EDIT) return areaOnProcessedChange(opt, setFieldValue);
    // filter the paths list to those for this area
    setPathsList(filterList(avalanchePaths, opt.value));

    //clear path
    setFieldValue('avalanche_path', nullOption, false);
    setFieldValue(
      'avalanche_program',
      avalanchePrograms.find((x) => x.value === opt.parentId),
    );

    setFieldValue('avalanche_target', nullOption, false);
    setPointList([]);
    setIsDisableTargeDd(true);
  };
  const areaOnProcessedChange = (opt, setFieldValue) => {
    // filter the paths list to those for this area
    setPathsList(filterList(avalanchePaths, opt.value));

    resetEventPaths();
    resetEventLocations();
    setFieldValue('avalanche_path', nullOption, false);
    setFieldValue('avalanche_target', nullOption, false);
    setPointList([]);
    setIsDisableTargeDd(true);
  };
  const filterList = (list, value) => {
    return list.filter(({ parentId }) => {
      return parentId === value;
    });
  };
  const filterTargetList = (opt) => {
    let t = areaPoints
      .filter((pt) => {
        return pt.areaId === opt.areaId; //do not use area code for target
      })
      .sort((a, b) => {
        return a.label < b.label ? -1 : a.label > b.label ? 1 : 0;
      });
    setPointList(mode === FORM_MODE.CREATE ? t.filter((x) => !x.isDisabled) : t);
  };
  const pathOnChange = (opt, setFieldValue, pathListToUse) => {
    if (isProcessed && mode === FORM_MODE.EDIT) return;
    //if the user picked a path first, we'll auto select the area/program
    const _area = avalancheAreas.find(({ value }) => {
      return value === opt.parentId;
    });

    //filter the area list to children of program
    setAreaList(filterList(avalancheAreas, _area.parentId));

    //set area value
    setFieldValue('avalanche_area', _area, false);

    //set program state form selected area
    setFieldValue(
      'avalanche_program',
      avalanchePrograms.find(({ value }) => {
        return value === _area.parentId;
      }),
      false,
    );

    //ensure the paths are children of area
    let p = filterList(pathListToUse || pathsList, _area.value);
    setPathsList(p);

    // set target
    setIsDisableTargeDd(false);
    setFieldValue('avalanche_target', '', false);
    filterTargetList(opt);
  };

  const dtoToEntity = (id, dto) => {
    const traceId = id ?? uuidv4();
    const entity = {
      id: traceId,
      provider: 'INTERNAL_INGESTION_' + currentUser.Env.toUpperCase(),
      trace_id: traceId,
      event_type: EventTypes.AvalancheControl,
      comment: dto.comment,
      userName: currentUser.UserName,
      userType: currentUser.UserType,
      userId: currentUser.SecurityIdentifierId,
      status: dto.status,
      ...mapFormToServerFormat(dto),
      lastUpdated: Date.now(), //must be after map
    };
    return entity;
  };

  const updateEntity = (setSubmitting, setErrors, dto, isCreateObs) => {
    const entity = dtoToEntity(eventId, dto);
    AWPLocalDB.avalancheData
      .update(eventId, entity)
      .then(async (updated) => {
        if (updated) {
          toast.success('Avalanche Control has been updated');
          if (isCreateObs) handleAddXnOccurrence(entity, setErrors);
        } else {
          const errors = { id: `Update failed. There was no Avalanche Control data with Id: ${eventId}` };
          setErrors(errors);
          if (errors.occurrence) setEventErrors({ Occurrence: errors.occurrence });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const createEntity = (setSubmitting, setErrors, dto, isCreateObs) => {
    const entity = dtoToEntity(null, dto);

    AWPLocalDB.avalancheData
      .add(entity)
      .then(async () => {
        toast.success('Avalanche Control has been created');
        if (isCreateObs) {
          handleAddXnOccurrence(entity, setErrors);
        } else {
          setEventTypeToActOn({ value: entity.event_type });
          setEventId(entity.id);
          setMode('edit');
        }
      })
      .catch((error) => {
        const errors = { InsertError: error };
        setErrors(errors);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const handleAddXnOccurrence = (event, setErrors) => {
    const eventType = eventTypes.find((e) => e.value === EventTypes.AvalancheOccurrence);
    let occurrence = createXnOccurrence(event);
    AWPLocalDB.avalancheData
      .add(occurrence)
      .then(async () => {
        const _state = {
          id: occurrence.id,
          eventType: EventTypes.AvalancheOccurrence,
          mode: FORM_MODE.EDIT,
          fromControl: true,
        };
        navigate(location.pathname, { state: _state });
        toast.success('Occurrence has been created');
        setEventId(occurrence.id);
        setEventTypeToActOn({ value: eventType });
        setMode(FORM_MODE.EDIT);
      })
      .catch((error) => {
        const errors = { InsertError: error };
        setErrors(errors);
      });
  };
  const handleSubmit = ({ values, setSubmitting, validateForm, setTouched, setErrors }, isCreateObs) => {
    let status = OfflineFormStatus.Valid;
    setFormStatus(status); //update display
    validateForm(values).then((errors) => {
      if (Object.keys(errors).length > 0) {
        setTouched(errors);
        status = OfflineFormStatus.Invalid;
      }
      setFormStatus(status);
      saveForm({ ...values, status }, setSubmitting, setErrors, isCreateObs);
    });
  };

  const saveForm = (values, setSubmitting, setErrors, isCreateObs) => {
    setSubmitting(true);

    if (mode === FORM_MODE.EDIT) {
      updateEntity(setSubmitting, setErrors, values, isCreateObs);
    } else {
      createEntity(setSubmitting, setErrors, values, isCreateObs);
    }
  };

  const handleSubmitProcessed = ({ values, setSubmitting, resetForm, setErrors }, isCreateObs) => {
    if (originalValues && values) {
      if (isCriticalFieldsUpdated(criticalFields, values, originalValues)) {
        setShowCriticalFieldsModal(true);
        setSubmitting(true);
        setSubmitting(false);
        return;
      }
    }
    setSubmitting(true);
    const body = {};
    const observations = [];

    for (const [key, value] of Object.entries(values)) {
      const field = key.replace(/_measured|_concurrencyControlNumber/g, '');
      const observationField = observationFields.includes(field);

      if (observationField) {
        const observationKey = key.includes('_measured')
          ? 'measured'
          : key.includes('_concurrencyControlNumber')
          ? 'concurrencyControlNumber'
          : 'value';

        const observationIndex = observations.findIndex((o) => o.key === field);

        if (observationIndex === -1) {
          observations.push({
            key: field,
            [observationKey]: value?.value ?? value,
          });
        } else {
          observations[observationIndex] = {
            ...observations[observationIndex],
            [observationKey]: value?.value ?? value,
          };
        }
      } else {
        body[key] = value?.value ?? value;
      }
    }

    body.observations = observations;
    if (criticalFields) {
      body.avalanche_path = values.avalanche_path.areaId;
      body.criticalFields = locationCriticalFieldsToEntity(criticalFields, values);
    } else {
      body.criticalFields = null;
    }

    // .NET 8 strict model binding rejects null for non-nullable numeric fields.
    // Convert null to 0 to match expected default behavior of .NET 7.
    // Backend depends on this behavior and we want to keep it.
    // There is a global solution for this in the backend to keep .NET 7 behavior, but it's not recommended.
    if (body.assistantBlaster === null) body.assistantBlaster = 0;

    const url = REST_API.PROCESSED_CONTROL + eventId;

    PutJson(url, body)
      .then(async (response) => {
        if (response.ok) {
          toast.success('Processed Control event has been updated');
          const loadedOccurrence = await response.json();
          afterFetchProcessed(loadedOccurrence);
          resetCriticalFields();
          resetForm();
        } else if (response.status === 412) {
          const errors = await response.json();
          toast.error('Update failed. Please review field error(s).', { autoClose: 5000 });
          setErrors(errors);
          if (errors.occurrence) setEventErrors({ Occurrence: errors.occurrence });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleBtnClose = () => {
    //props.resetForm();
    if (fromMenu) navigate(location.pathname, { state: null });
    setMode(FORM_MODE.LIST);
  };
  const handleCriticalSection = (props) => {
    setShowCriticalFieldsModal(false);
    setLoadingCriticalField(true);

    const url = REST_API.PROCESSED_LINKED_EVENTS + eventId;

    GetJson(url)
      .then((response) => response.json())
      .then((entity) => {
        const _entity = entity;
        if (_entity) {
          setCriticalFields(_entity);
          getEventPaths(_entity);
          getEventLocations(_entity);
          setShowCriticalFields(true);
        }
      })
      .finally(() => {
        setLoadingCriticalField(false);
      });
  };
  const resetEventPaths = () => {
    if (!eventPaths) return;
    if (Array.isArray(eventPaths)) {
      let _eventPaths = [];
      eventPaths.map((c) => {
        _eventPaths.push({ eventId: c.eventId, value: null });
      });
      setEventPaths(_eventPaths);
      return;
    }
  };
  const checkEventPaths = (values) => {
    if (!eventPaths) return false;
    if (!values || !values.avalanche_path || !values.avalanche_path || !values.avalanche_path.areaId) return false;
    if (Array.isArray(eventPaths)) {
      const ep = eventPaths.find((c) => {
        return c.eventId !== eventId && !c.value;
      });
      if (ep) return false;
    }
    return true;
  };
  const getEventPathValue = (id) => {
    if (Array.isArray(eventPaths)) {
      const ep = eventPaths.find((c) => {
        return c.eventId === id;
      });
      if (ep) return ep.value;
    }
    return null;
  };
  const updateEventPath = (id, v) => {
    let _eventPaths = [];
    let found = false;
    if (Array.isArray(eventPaths)) {
      eventPaths.map((c) => {
        if (c.eventId === id) {
          _eventPaths.push({ eventId: id, value: { ...v } });
          found = true;
        } else {
          _eventPaths.push(c);
        }
      });
      if (!found) _eventPaths.push({ eventId: id, value: v });
      if (_eventPaths && Array.isArray(_eventPaths) && _eventPaths.length > 0) setEventPaths(_eventPaths);
      return;
    }
  };
  const getEventPaths = (entity) => {
    if (!entity) return;
    let _eventPaths = [];
    let _children = entity.ChildAvalancheProcessedEvents;
    if (Array.isArray(_children)) {
      _children.map((c) => {
        let _gChildren = c.ChildAvalancheProcessedEvents;
        if (Array.isArray(_gChildren)) {
          _gChildren.map((gc) => {
            if (checkEventType(gc.EventTypeNm)) {
              _eventPaths.push({ eventId: gc.EventId, value: null });
            }
          });
        }
        if (checkEventType(c.EventTypeNm)) {
          _eventPaths.push({ eventId: c.EventId, value: null });
        }
      });
    }
    if (checkEventType(entity.EventTypeNm)) {
      _eventPaths.push({ eventId: entity.EventId, value: null });
    }
    if (_eventPaths && Array.isArray(_eventPaths) && _eventPaths.length > 0) setEventPaths(_eventPaths);
  };
  // true = failed /false = passed
  const validCriticalFields = (values) => {
    if (!criticalFields || !dto) return false;
    const evtType = findEventTypeNmById(criticalFields, dto.eventId);
    if (evtType && checkEventType(evtType)) {
      if (!criticalFields.ChildAvalancheProcessedEvents || criticalFields.ChildAvalancheProcessedEvents.length === 0)
        return false;
      if (!eventPaths) return true;
      if (!checkEventPaths(values)) return true;
      if (IsOnlyEventPath(criticalFields)) return false;
      return !checkEventLocations();
    }
    return false;
  };

  const resetCriticalFields = () => {
    setCriticalFields(null);
    setEventPaths(null);
    setEventSegment(null);
    setEventFrom(null);
    setEventTo(null);
    setEventLocations(null);
  };

  const resetEventLocations = () => {
    if (!eventLocations) return;
    if (Array.isArray(eventLocations)) {
      let _eventLocations = [];
      eventLocations.map((c) => {
        _eventLocations.push({ eventId: c.eventId, segment: null, from: null, to: null });
      });
      setEventLocations(_eventLocations);
      return;
    }
  };
  //only for RC or Phased = non-eventPaths
  const getEventLocations = (entity) => {
    if (!entity) return;
    let _eventLocations = [];
    let _children = entity.ChildAvalancheProcessedEvents;
    if (Array.isArray(_children)) {
      _children.map((c) => {
        let _gChildren = c.ChildAvalancheProcessedEvents;
        if (Array.isArray(_gChildren)) {
          _gChildren.map((gc) => {
            if (!checkEventType(gc.EventTypeNm)) {
              _eventLocations.push({ eventId: gc.EventId, segment: null, from: null, to: null });
            }
          });
        }
        if (!checkEventType(c.EventTypeNm)) {
          _eventLocations.push({ eventId: c.EventId, segment: null, from: null, to: null });
        }
      });
    }
    if (!checkEventType(entity.EventTypeNm)) {
      _eventLocations.push({ eventId: entity.EventId, segment: null, from: null, to: null });
    }
    if (_eventLocations && Array.isArray(_eventLocations) && _eventLocations.length > 0)
      setEventLocations(_eventLocations);
  };
  //check if all event types are event path(RC or RO)
  const IsOnlyEventPath = (entity) => {
    if (!entity) return false;
    let _eventLocations = []; //only for RC or Phased = non-eventPaths
    let _eventPathLocations = []; //only for event path(RC or RO)
    let _children = entity.ChildAvalancheProcessedEvents;
    if (Array.isArray(_children)) {
      _children.map((c) => {
        let _gChildren = c.ChildAvalancheProcessedEvents;
        if (Array.isArray(_gChildren)) {
          _gChildren.map((gc) => {
            if (!checkEventType(gc.EventTypeNm)) {
              _eventLocations.push(gc);
            } else {
              _eventPathLocations.push(gc);
            }
          });
        }
        if (!checkEventType(c.EventTypeNm)) {
          _eventLocations.push(c);
        } else {
          _eventPathLocations.push(c);
        }
      });
    }
    if (!checkEventType(entity.EventTypeNm)) {
      _eventLocations.push(entity);
    } else {
      _eventPathLocations.push(entity);
    }
    if (
      _eventLocations.length === 0 &&
      _eventPathLocations &&
      Array.isArray(_eventPathLocations) &&
      _eventPathLocations.length > 0
    )
      return true;
    return false;
  };
  const checkEventLocations = () => {
    if (!eventLocations) return false;
    if (Array.isArray(eventLocations)) {
      const ep = eventLocations.find((c) => {
        return !c.segment && !c.from && !c.to;
      });
      if (ep) return false;
    }
    return true;
  };
  const getEventLocationValue = (id, type) => {
    if (Array.isArray(eventLocations)) {
      const ep = eventLocations.find((c) => {
        return c.eventId === id;
      });
      if (ep) {
        if (type === 'segment') return ep.segment;
        if (type === 'from') return ep.from;
        if (type === 'to') return ep.to;
      }
    }
    return null;
  };
  const updateEventLocation = (id, v, type) => {
    let _eventLocations = [];
    if (Array.isArray(eventLocations)) {
      eventLocations.map((c) => {
        if (type === 'segment') _eventLocations.push({ eventId: c.eventId, segment: { ...v }, from: null, to: null });
        if (type === 'from') _eventLocations.push({ eventId: c.eventId, segment: null, from: { ...v }, to: c.to });
        if (type === 'to') _eventLocations.push({ eventId: c.eventId, segment: null, from: c.from, to: { ...v } });
      });

      if (_eventLocations && Array.isArray(_eventLocations) && _eventLocations.length > 0)
        setEventLocations(_eventLocations);
      return;
    }
  };
  const breadCrumb = () => {
    const entity = findEvent(loadedData, eventId);
    const name = entity?.internal.event_type?.label;
    return name ? name : eventId;
  };

  const Buttons = ({ props }) => {
    return mode !== FORM_MODE.VIEW ? (
      <>
        <div className="mr-3" style={{ float: 'left' }}>
          {!showCancelConfirm && (
            <Button
              className="mr-2"
              variant="outline-primary"
              style={{ marginTop: '40px' }}
              onClick={() => {
                setShowCancelConfirm(true);
              }}
            >
              {'Close'}
            </Button>
          )}

          {!showCancelConfirm && isAvalancheTech && hasAccess([Permissions.WriteAvalancheForm]) && (
            <Button
              type="button"
              variant="primary"
              className="mr-2"
              style={{ marginTop: '40px' }}
              onClick={() => {
                if (!isProcessed) {
                  handleSubmit(props, false);
                } else {
                  if (props.values.timestamp != dto.timestamp) {
                    setShowSaveConfirm(true);
                  } else {
                    handleSubmitProcessed(props);
                  }
                }
              }}
              disabled={
                isProcessed
                  ? !props.isValid || !props.dirty || props.isSubmitting || validCriticalFields(props.values)
                  : props.isSubmitting
              }
            >
              {props.isSubmitting ? (
                <>
                  <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                <span>{buttonLabel}</span>
              )}
            </Button>
          )}
          {!showCancelConfirm && isAvalancheTech && hasAccess([Permissions.WriteAvalancheForm]) && !isProcessed && (
            <Button
              type="button"
              className="ml-2"
              variant="primary"
              style={{ marginTop: '40px' }}
              onClick={() => {
                handleSubmit(props, true);
              }}
              disabled={
                isProcessed
                  ? !props.isValid || !props.dirty || props.isSubmitting || !props.values.aval_is_avalanche_success
                  : props.isSubmitting || !props.values.aval_is_avalanche_success
              }
            >
              {props.isSubmitting ? (
                <>
                  <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                <span>{'Save & Create AvOb'}</span>
              )}
            </Button>
          )}
          {showCancelConfirm && (
            <AwpYesNoDialog
              action={() => handleBtnClose(props)}
              setShowYesNoConfirm={setShowCancelConfirm}
              msg={'Are you sure you want to close?'}
              props={{}}
            />
          )}
        </div>
      </>
    ) : (
      <Button
        className="mr-2"
        variant="outline-primary"
        style={{ marginTop: '40px' }}
        onClick={() => {
          handleBtnClose(props);
        }}
      >
        {'Close'}
      </Button>
    );
  };

  Buttons.propTypes = {
    title: PropTypes.string,
    props: PropTypes.any,
    resetForm: PropTypes.func,
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    dirty: PropTypes.bool,
  };

  return (
    <>
      {loading ? (
        <Container>
          <Spinner animation="border" />
        </Container>
      ) : (
        <>
          <div style={{ paddingBottom: '10px' }}>
            <span className="awp-text-btn" onClick={() => setMode(FORM_MODE.LIST)}>
              {breadcrumbName}
            </span>
            <span className="mx-3">&gt;</span>
            <span>{breadCrumb()}</span>
          </div>
          <Formik
            enableReinitialize
            validateOnMount
            validateOnBlur
            validateOnChange
            initialTouched={getInitialTouched(dto, mode === 'edit')}
            initialValues={dto}
            validationSchema={validationSchema}
            onSubmit={isProcessed ? handleSubmitProcessed : handleSubmit}
          >
            {(props) => (
              <>
                {/* {props.errors && <pre>{JSON.stringify(props.errors, null, 2)}</pre>} */}
                {/* {props.values && <pre>{JSON.stringify(props.values, null, 2)}</pre>} */}
                <div className="d-flex">
                  <h1 className="flex-grow-1">{title}</h1>
                </div>
                <Form>
                  <Card
                    border="light"
                    className="page-card"
                    style={{ border: '0px', boxShadow: '0px 0px 0px #000000' }}
                  >
                    <Card.Body className="pr-pl-pt-0">
                      {(mode === FORM_MODE.EDIT || mode === FORM_MODE.VIEW) && dto && isProcessed && (
                        <div className="mb-3">
                          <FormTextReadOnly label="Created By" textValue={dto.createUserId} />
                          <FormTextReadOnly label="Last Updated" textValue={dto.lastUpdatetimestamp} />
                          <FormTextReadOnly label="Last Updated By" textValue={dto.lastUpdateUserId} />
                        </div>
                      )}

                      <FormCheckbox
                        readOnly={readOnly}
                        useMargin={true}
                        id="aval_is_outside"
                        name="aval_is_outside"
                        label="Outside Highway Corridor"
                        value={false}
                      />
                      <FormSelect
                        className="avalanche-select"
                        readOnly={readOnly}
                        name="avalanche_program"
                        useMargin={true}
                        id="avalanche_program"
                        label="Program"
                        postChange={(e) => {
                          programOnChange(e, props.setFieldValue);
                        }}
                        options={avalanchePrograms}
                        isMulti={false}
                      />

                      <FormSelect
                        className="avalanche-select"
                        readOnly={readOnly}
                        name="avalanche_area"
                        useMargin={true}
                        id="avalanche_area"
                        label="Avalanche Area"
                        postChange={(e) => {
                          areaOnChange(e, props.setFieldValue);
                        }}
                        options={areaList}
                        isMulti={false}
                      />
                      <FormSelect
                        className="avalanche-select"
                        readOnly={readOnly}
                        name="avalanche_path"
                        useMargin={true}
                        id="avalanche_path"
                        label={'Path' + (isProcessed ? '' : ' (required)')}
                        postChange={(e) => {
                          pathOnChange(e, props.setFieldValue);
                        }}
                        options={pathsList}
                        isMulti={false}
                        focus={isDuplicated && mode === FORM_MODE.EDIT}
                      />
                      <FormSelect
                        className="avalanche-select"
                        readOnly={readOnly || isProcessed}
                        name="avalanche_target"
                        id="avalanche_target"
                        useMargin={true}
                        label="Target"
                        options={pointList}
                        isDisabled={isDisableTargeDd}
                        isMulti={false}
                        isClearable={true}
                      />
                      <FormSelect
                        readOnly={readOnly || isProcessed}
                        name="aval_cntrl_type_cd"
                        useMargin={true}
                        id="aval_cntrl_type_cd"
                        label={'Control Type' + (isProcessed ? '' : ' (required)')}
                        options={controlTypes}
                        isMulti={false}
                      />
                      <OccurrenceOptionalFields
                        readOnly={readOnly}
                        useMargin={true}
                        props={props}
                        eventProperties={controlEventProperties}
                        isAccordion={false}
                      />
                      <div className="customWidthDatePicker">
                        <FormDatePicker
                          readOnly={readOnly}
                          useMargin={true}
                          label="Initiation Date/Time"
                          id="timestamp"
                          name="timestamp"
                          showTimeSelect={true}
                          showTime={{ isEnabled: true }}
                          timeFormat="HH:mm"
                          timeIntervals={30}
                          dateFormat={['yyyy-MM-dd HH:mm']}
                          hasButton={true}
                          onButtonClick={() => {
                            props.setFieldValue('timestamp', new Date());
                          }}
                        />
                      </div>
                      <FormGpsPoint
                        id="location"
                        name="location"
                        useMargin={true}
                        label="Location"
                        setFieldValue={props.setFieldValue}
                        readOnly={readOnly}
                      />
                      <FormCheckbox
                        useMargin={true}
                        readOnly={readOnly || isProcessed}
                        id="aval_is_avalanche_success"
                        name="aval_is_avalanche_success"
                        label="Occurrence success"
                        value={false}
                      />
                      <FormCheckbox
                        readOnly={readOnly || isProcessed}
                        useMargin={true}
                        id="aval_is_detonation_failure"
                        name="aval_is_detonation_failure"
                        label="Failure to detonate"
                        value={false}
                      />
                      <FormSelect
                        readOnly={readOnly}
                        useMargin={true}
                        name="blaster"
                        id="blaster"
                        label="Blaster"
                        options={avalancheTechs.options}
                        isMulti={false}
                      />
                      <FormSelect
                        readOnly={readOnly}
                        useMargin={true}
                        name="assistantBlaster"
                        id="assistantBlaster"
                        label="Assistant Blaster"
                        options={avalancheTechs.options}
                        isMulti={false}
                        isClearable={true}
                      />
                      <FormTextArea readOnly={readOnly} label="Comment" id="comment" name="comment" rows={3} />
                      {mode !== FORM_MODE.CREATE ? (
                        <div>
                          <b>Form Status:</b>{' '}
                          <span
                            style={formStatus === OfflineFormStatus.Invalid ? { color: 'red' } : { color: 'black' }}
                          >
                            {formStatus}
                          </span>
                        </div>
                      ) : null}
                      <Buttons title=" " props={props} />
                      <AwpYesNoModal
                        showYesNoModal={showSaveConfirm}
                        setShowYesNoModal={setShowSaveConfirm}
                        onClickYes={() => {
                          setShowSaveConfirm(false);
                          handleSubmitProcessed(props);
                        }}
                        title="Save"
                        body="A change in Date/Time of Control events cascades to its related child Xn Occurrence. Do you still want to save?"
                      />
                      {loadingCriticalField && (
                        <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                      )}
                      {!loadingCriticalField &&
                        !showCriticalFieldsModal &&
                        showCriticalFields &&
                        eventId > 0 &&
                        criticalFields && (
                          <div style={{ display: 'block', clear: 'both', paddingTop: '1rem' }}>
                            <ProcessedCriticalFieldsEdit
                              dto={criticalFields}
                              setCriticalFields={setCriticalFields}
                              criticalFields={criticalFields}
                              updatedDto={props.values}
                              eventPaths={eventPaths}
                              updateEventPath={updateEventPath}
                              getEventPathValue={getEventPathValue}
                              eventSegment={eventSegment}
                              setEventSegment={setEventSegment}
                              eventFrom={eventFrom}
                              setEventFrom={setEventFrom}
                              eventTo={eventTo}
                              setEventTo={setEventTo}
                              isParent={true}
                              eventLocations={eventLocations}
                              updateEventLocation={updateEventLocation}
                              getEventLocationValue={getEventLocationValue}
                            />
                            <Row style={{ height: '1rem' }} />
                          </div>
                        )}
                      {!loadingCriticalField &&
                        !showCriticalFieldsModal &&
                        showCriticalFields &&
                        eventId > 0 &&
                        criticalFields && (
                          <div style={{ display: 'block', clear: 'both', paddingTop: '1rem' }}>
                            {criticalFields &&
                              criticalFields.ChildAvalancheProcessedEvents &&
                              Object.keys(criticalFields.ChildAvalancheProcessedEvents).map((d, idx) => {
                                return (
                                  <>
                                    <ProcessedCriticalFieldsEdit
                                      key={idx}
                                      dto={criticalFields.ChildAvalancheProcessedEvents[d]}
                                      setCriticalFields={setCriticalFields}
                                      criticalFields={criticalFields}
                                      updatedDto={props.values}
                                      eventPaths={eventPaths}
                                      updateEventPath={updateEventPath}
                                      getEventPathValue={getEventPathValue}
                                      eventSegment={eventSegment}
                                      setEventSegment={setEventSegment}
                                      eventFrom={eventFrom}
                                      setEventFrom={setEventFrom}
                                      eventTo={eventTo}
                                      setEventTo={setEventTo}
                                      isParent={false}
                                      eventLocations={eventLocations}
                                      updateEventLocation={updateEventLocation}
                                      getEventLocationValue={getEventLocationValue}
                                    />
                                    <Row style={{ height: '1rem' }} />
                                  </>
                                );
                              })}
                          </div>
                        )}
                    </Card.Body>
                  </Card>
                </Form>
                <AwpYesNoModal
                  showYesNoModal={showCriticalFieldsModal}
                  setShowYesNoModal={setShowCriticalFieldsModal}
                  onClickYes={() => {
                    handleCriticalSection(props);
                  }}
                  title="Warning"
                  body="Changing the location information will result in the related elements needing to be set again. Are you sure you want to proceed?"
                />
              </>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setEventErrors} errors={eventErrors} />
        </>
      )}
    </>
  );
};

AvalancheControlEdit.propTypes = {
  setMode: PropTypes.func,
  mode: PropTypes.string,
  setEventId: PropTypes.func,
  eventId: PropTypes.any,
  setLoading: PropTypes.func,
  loading: PropTypes.bool,
  setFieldValue: PropTypes.func,
  validateField: PropTypes.func,
  setFieldTouched: PropTypes.func,
  values: PropTypes.any,
  resetForm: PropTypes.func,
  fullData: PropTypes.array,
  setEventTypeToActOn: PropTypes.func,
  isProcessed: PropTypes.bool,
  isRoute: PropTypes.bool,
  isDuplicated: PropTypes.bool,
};

export default AvalancheControlEdit;
